import mapboxgl from "mapbox-gl";
import { useSignal } from "@preact/signals-react";
import { useRef } from "react";
import { FotClient } from "imagica-corekit/dist/base/api/FotClient";
import { getIt } from "../../../../uikit/getIt";
import { get, isEmpty } from "lodash";
import { ResultGoogleType } from "imagica-corekit/dist/base/api/mapTyped/GoogleMap";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";
import { FeatureType, PhotoType } from "imagica-corekit/dist/base/api/mapTyped/MapType";
import func from "@uikit/func";

type tipErrorType = {
  key?: string;
  value?: string;
};
export class MapViewBloc {
  mapboxgl: typeof mapboxgl;
  constructor(reMapboxgl: typeof mapboxgl) {
    this.mapboxgl = reMapboxgl;
  }
  brainClient = getIt(FotClient);
  map = useSignal(<any>null);
  id = useSignal("");
  marker = useSignal(<any>{});
  mapMarkerRef = useRef(null);
  isFirstLoad = useSignal(true);
  statusList = useSignal<Array<string>>([]);
  locationNoneList = useSignal<Array<string>>([]);
  mapList = useSignal<Array<ResultGoogleType>>([]); // 地图中的marker列表数据
  photoList = useSignal<Array<PhotoType>>([]); // 地图中的marker列表数据
  isMultInput = useSignal(false);
  mapLoading = useSignal(false);
  markerIcon = "https://dopniceu5am9m.cloudfront.net/static/blocks/icon/map_marker.png";
  tipError = useSignal<tipErrorType>({});

  async init(id: string, results: string) {
    this.initMap(id, [-115.1765067, 36.1129455]); // 默认中心点经纬度
    this.handleInputResults(results);
  }

  reloadMap(results: string) {
    this.handleInputResults(results);
  }

  initMap(id: string, coordinates: mapboxgl.LngLatLike) {
    const locationLatLng: mapboxgl.LngLatLike = (coordinates as mapboxgl.LngLatLike) || [-115.1765067, 36.1129455];

    mapboxgl.accessToken = settings.mapKey;

    this.id.value = id;
    this.map.value = new mapboxgl.Map({
      container: id,
      style: "mapbox://styles/mapbox/light-v9",
      center: locationLatLng,
      zoom: 12,
    });
  }

  resetData() {
    this.mapList.value = [];
    this.statusList.value = [];
    this.locationNoneList.value = [];
    this.mapLoading.value = true;
    this.tipError.value = {};
  }

  handleInputResults(input: string) {
    const arr: Array<string> = JSON.parse(input);
    this.isMultInput.value = arr.length > 1;
    this.resetData();

    const result = arr.map((x: string, index: number) => {
      this.handleInput(x, index);
      return "pending";
    });

    if (this.isMultInput.value) {
      this.statusList.value = result;
    }
  }

  getLoactionAndKeywordData(text: string) {
    const arr = text.split("\n").filter((x: string) => x);
    const obj = {
      keyword: "",
      location: "",
    };

    arr.forEach(x => {
      const resultArr = x.split(":").filter((j: string) => j.trim());
      if (isEmpty(resultArr)) return;
      if (resultArr[0] === "keyword") {
        obj["keyword"] = resultArr?.[1]?.trim() || "";
      }
      if (resultArr[0] === "location") {
        obj["location"] = resultArr?.[1]?.trim() || "";
      }
    });

    return obj;
  }

  async handleInput(input: string, index: number) {
    try {
      this.mapLoading.value = true;
      const data = await this.brainClient.map.postDomainMapping(input);

      const text = get(data, "choices.[0].text", "");
      const params = this.getLoactionAndKeywordData(text);
      let locationLatLng: Array<number> = [];
      if (params.location === "NONE") {
        // params.location = "";
        // this.locationNoneList.value.push(params.keyword)
        // this.tipError.value = {
        //   key: 'locationTip',
        // }
        const res = await func.getLocation();
        if (res === "userDenied") {
          params.location = "";
        } else {
          locationLatLng = [res.lng, res.lat];
        }
      } else if (!isEmpty(params.location)) {
        const locationData = await this.brainClient.map.getMapAddressGps(params.location);
        const locationObj = get(locationData, "results.[0].geometry.location", { lng: 0, lat: 0 });
        locationLatLng = [locationObj.lng, locationObj.lat];
      }
      const res = await this.brainClient.map.getMapTextSearch(params.keyword, locationLatLng);

      if (isEmpty(res.results)) {
        throw `"${params.keyword}" did not return data.`;
      }
      res?.results?.forEach((x: ResultGoogleType) => {
        x.keyword = params.keyword;
      });
      if (this.isMultInput.value) {
        this.mapList.value = this.mapList.value.concat([]);
        const obj = get(res, "results.[0]", {});
        this.mapList.value = [...this.mapList.value, obj];
      } else {
        const arr = get(res, "results", []);
        this.mapList.value = arr.slice(0, 15);
      }

      setTimeout(() => {
        this.load();
      });
    } catch (error) {
      const value = typeof error === "string" ? error : "Get failed " + input;
      this.tipError.value = {
        key: "error",
        value,
      };
      console.error("error::::", error);
    } finally {
      this.mapLoading.value = false;
    }
  }

  async getMapAddressGps(keyword: string) {
    return await this.brainClient.map.getMapAddressGps(keyword);
  }

  setFitView(coordinates: Array<mapboxgl.LngLatLike>) {
    const bounds = new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]);
    for (const coord of coordinates) {
      bounds.extend(coord);
    }

    this.map.value.fitBounds(bounds, {
      padding: 30,
    });
  }

  getMapFeaturesAndLocation() {
    const featureArr: Array<FeatureType> = [];
    const locationArr: Array<mapboxgl.LngLatLike> = [];
    this.mapList.value.forEach((address: ResultGoogleType) => {
      const locationObj = get(address, "geometry.location", { lng: 0, lat: 0 });
      const locationLatLng = [locationObj.lng, locationObj.lat] as mapboxgl.LngLatLike;
      const obj = {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [locationObj.lng || 0, locationObj.lat || 0],
        },
        properties: {
          title: address.name || "",
        },
      };
      featureArr.push(obj);
      locationArr.push(locationLatLng);
    });
    return {
      features: featureArr,
      Location: locationArr,
    };
  }

  load() {
    if (isEmpty(this.map.value)) {
      return;
    }
    if (this.isFirstLoad.value === false) {
      this.map.value.removeLayer("points");
      this.map.value.removeSource("points");
    }
    const specificData = this.getMapFeaturesAndLocation();
    if (this.isFirstLoad.value) {
      // Add an image to use as a custom marker
      this.map.value.loadImage(
        `${settings.S3CDN}${settings.viewAssetsPath}map_marker.png`,
        (error: any, image: any) => {
          if (error) throw error;
          this.map.value.addImage("custom-marker", image);
        }
      );
    }

    this.map.value.addSource("points", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: specificData.features,
      },
    });

    this.map.value.addLayer({
      id: "points",
      type: "symbol",
      source: "points",
      layout: {
        "icon-image": "custom-marker",
        "icon-size": 0.5,
        "text-field": ["get", "title"],
        "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
        "text-offset": [0, 1.25],
        "text-anchor": "top",
      },
    });
    this.isFirstLoad.value = false;
    if (specificData.Location.length > 1) {
      this.setFitView(specificData.Location);
    } else {
      this.map.value.setCenter(specificData.Location[0]);
    }
  }

  getCustomId(address: ResultGoogleType, id: string, index: number) {
    return get(address, "geometry.location.lng", "marker") + id + index;
  }

  getAddressDetail(address: ResultGoogleType) {
    return address.formatted_address;
  }

  zoomIn(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) {
    e.stopPropagation();
    this.map.value.zoomIn({
      duration: 1000,
    });
  }
  zoomOut(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) {
    e.stopPropagation();
    this.map.value.zoomOut({
      offset: [80, 60],
    });
  }

  async reloadLocation() {
    try {
      const res = await func.getLocation();
      if (res === "userDenied") {
        throw "User refused permission to open";
      }
      const locationLatLng = [res.lng, res.lat];

      this.locationNoneList.value.forEach(async (x: string) => {
        const res = await this.brainClient.map.getMapTextSearch(x, locationLatLng);
        res?.results?.forEach((j: ResultGoogleType) => {
          j.keyword = x;
        });
        if (this.isMultInput.value) {
          const idx = this.mapList.value.findIndex((j: ResultGoogleType) => j.keyword === x);
          const obj = get(res, "results.[0]", {});
          idx === -1 ? this.mapList.value.push(obj) : (this.mapList.value[idx] = obj);
        } else {
          const arr = get(res, "results", []);
          this.mapList.value = arr.slice(0, 15);
        }
        if (isEmpty(this.mapList.value)) {
          return;
        }
        setTimeout(() => {
          this.load();
        });
      });
    } catch (error: unknown) {
      const value = typeof error === "string" ? error : `Get failed "${this.locationNoneList.value.join(",")}"`;
      this.tipError.value = {
        key: "error",
        value,
      };
    } finally {
      if (this.tipError.value.key !== "error") {
        this.tipError.value = {};
      }
    }
  }
}
